import { Badge, Button, Flex, H3, P } from '@dnb/eufemia';
import classnames from 'classnames';
import { type JSX } from 'react';

import Card from '@/components/Card';
import { ApiAppClientResponse } from '@/openapi/schema';
import { isEmptyString } from '@/utils/textUtils';

import { ViewType } from '../schemas';

import style from './index.module.css';

export type ClientTileProps = {
  client: ApiAppClientResponse;
  showView: (view: ViewType, query?: string) => void;
};

export default function ClientTile({ client, showView }: ClientTileProps): JSX.Element {
  const hasDescription = !isEmptyString(client.description);

  return (
    <Card className={style['ClientTile']}>
      <Flex.Container stretch>
        <Flex.Item grow>
          <H3 bottom="small">{client.name || 'Unnamed Client'}</H3>
        </Flex.Item>
        <Badge content={client.liveMode ? 'Live' : 'Test'} />
      </Flex.Container>
      <div className={style['InformationGrid']}>
        <InformationLabel text="Client ID" />
        <P>{client.ciamClientId}</P>
        <InformationLabel text="Description" />
        <P className={classnames({ NoData: !hasDescription })}>
          {hasDescription ? client.description : 'No description'}
        </P>

        <InformationLabel text="App ID" />
        {client.appId ? (
          <P>
            <Button
              onClick={() => showView('apps', client.appId)}
              size="small"
              tooltip="Show app details"
              variant="tertiary"
            >
              {client.appId}
            </Button>
          </P>
        ) : (
          <P className={classnames({ NoData: true })}>N/A</P>
        )}
        <InformationLabel text="Ciam App ID" />

        {client.ciamAppId ? (
          <P>
            <Button
              onClick={() => showView('clients', client.ciamAppId as string)}
              size="small"
              tooltip="Show all clients for this Ciam App ID"
              variant="tertiary"
            >
              {client.ciamAppId}
            </Button>
          </P>
        ) : (
          <P className={classnames({ NoData: true })}>N/A</P>
        )}

        <InformationLabel text="Scopes" />

        <div>
          {client.scopes.map((s) => (
            <P key={s}>{s}</P>
          ))}
        </div>
      </div>
    </Card>
  );
}

function InformationLabel({ text }: { text: string }): JSX.Element {
  return <P weight="medium">{text}:</P>;
}
